import { getUnsavedChangesSelector, UnsavedChangesActions } from '@abovo/webvis-data-access';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import { inject, Injectable, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { KpiForm } from '../models/intermediate-report.models';

/**
 * Determines which save path the grid container takes when the FAB is clicked.
 * 'direct' saves the report straight away, 'elaboration' first opens the specialist elaboration dialog.
 */
export type ReportSaveMode = 'direct' | 'elaboration';

@Injectable()
export class SaveLoadHelperService {
   private overlayCounter = 0;
   private overlayRef?: OverlayRef;
   set loadingOverlay(overlay: CdkPortal) {
      this._loadingOverlay = overlay;
      this.showHideOverlay();
   }
   private _loadingOverlay?: CdkPortal;
   private hideTimeout?: ReturnType<typeof setTimeout>;

   /** Used to start a save in any needed component from the FAB. The mode decides which save path is taken. */
   saveEvent = signal<{ event: MouseEvent; mode: ReportSaveMode } | null>(null);

   /** List of actual keyperformance indicator ids that need to be deleted */
   deleteIdsList = new Set<number>();

   private store = inject(Store);
   private noUnsavedChanges = this.store.selectSignal(getUnsavedChangesSelector);

   constructor(private overlay: Overlay) {}

   /**
    * Derives the page wide unsaved changes state from the report form, the KPI forms and the pending KPI deletes.
    * Only dispatches when the state changes.
    */
   updateUnsavedChanges(reportForm: FormGroup, kpiForms: Map<number, KpiForm>): void {
      const kpiFormDirty = Array.from(kpiForms.values()).some((kpiForm) => kpiForm.formGroup.dirty);
      const noUnsavedChanges = !reportForm.dirty && !kpiFormDirty && !this.deleteIdsList.size;
      if (noUnsavedChanges === this.noUnsavedChanges()) {
         return;
      }
      this.store.dispatch(
         UnsavedChangesActions.setUnsavedChanges({
            noUnsavedChanges,
            message: {
               title: 'general.leave_page_confirmation',
               message: 'intermediate_report.not_saved_changes',
               cancelButton: 'general.no',
               removeButton: 'general.yes',
            },
         }),
      );
   }

   overlayToggle(show: boolean) {
      this.overlayCounter += show ? 1 : -1;
      this.showHideOverlay();
   }

   private showHideOverlay() {
      if (!this.overlayRef) {
         this.overlayRef = this.overlay.create({ disposeOnNavigation: true, hasBackdrop: true, minHeight: '100%', minWidth: '100%' });
      }
      if (this.overlayCounter > 0 && !this.overlayRef.hasAttached() && this._loadingOverlay) {
         clearTimeout(this.hideTimeout);
         this.overlayRef.attach(this._loadingOverlay);
      }
      if (this.overlayCounter <= 0 && this.overlayRef.hasAttached()) {
         // add in a timeout to prevent blinking in and out when loading things in quick succession
         this.hideTimeout = setTimeout(() => {
            this.overlayRef?.detach();
         }, 300);
      }
   }
}
